// Router mínimo y explícito (sin dependencia): una tabla de rutas con segmentos `:param`.
// Los otros carriles agregan sus rutas en `routes` (App.tsx) apuntando a `src/features/<feature>/`.
import { useEffect, useState, type MouseEvent, type ReactNode } from 'react';

export type Params = Record<string, string>;
export interface Route { pattern: string; render: (params: Params) => ReactNode }

export function matchRoute(pattern: string, pathname: string): Params | null {
  const p = pattern.split('/').filter(Boolean);
  const s = pathname.split('/').filter(Boolean);
  if (p.length !== s.length) return null;
  const params: Params = {};
  for (let i = 0; i < p.length; i++) {
    const seg = p[i]!;
    const val = s[i]!;
    if (seg.startsWith(':')) {
      try { params[seg.slice(1)] = decodeURIComponent(val); } catch { return null; }
    } else if (seg !== val) {
      return null;
    }
  }
  return params;
}

export function navigate(to: string) {
  window.history.pushState(null, '', to);
  window.dispatchEvent(new PopStateEvent('popstate'));
  window.scrollTo(0, 0);
}

export function usePathname(): string {
  const [path, setPath] = useState(() => window.location.pathname);
  useEffect(() => {
    const on = () => setPath(window.location.pathname);
    window.addEventListener('popstate', on);
    return () => window.removeEventListener('popstate', on);
  }, []);
  return path;
}

export function Router({ routes, fallback }: { routes: Route[]; fallback: ReactNode }) {
  const path = usePathname();
  for (const r of routes) {
    const params = matchRoute(r.pattern, path);
    if (params) return <>{r.render(params)}</>;
  }
  return <>{fallback}</>;
}

/** Enlace interno: navega sin recargar, pero respeta ctrl/cmd-clic y "abrir en otra pestaña". */
export function Link({ to, children, className }: { to: string; children: ReactNode; className?: string }) {
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(to);
  };
  return <a href={to} className={className} onClick={onClick}>{children}</a>;
}
